import {Component, OnInit} from '@angular/core';
import {MatDialog, MatDialogRef} from '@angular/material';
import {CreateFloorDialogComponent} from '../../components/create-floor-dialog/create-floor-dialog.component';
import {select, Store} from '@ngrx/store';

import * as fromRoot from '../../../reducers';
import * as fromAfbApi from '../../../afb-api/reducers';
import * as fromFloorPlan from '../../../floor-plan/reducers/index';
import * as fromFloorPlanEditor from '../../reducers/index';
import {DeleteFloor, PostFloor} from '../../../afb-api/actions/floor-plan.actions';
import {AuthService, UtilService} from '../../../core';
import {Observable, combineLatest} from 'rxjs';
import {distinctUntilChanged, filter, withLatestFrom} from 'rxjs/operators';
import {Floor} from '../../../afb-api/models/floor';
import {DeselectFloor, ResetFloorImage, SelectFloor} from '../../actions/floor-plan-editor-view.actions';
import {RemoveAllDeviceMarker} from '../../actions/floor-plan-editor-device-marker.actions';
import {DeviceWithUuid, DeviceWithUuidAndStatus} from '../../../afb-api/models/device';
import {OrganizationWithToken} from '../../../core/models/core';
import {NegativeConfirmComponent} from '../../../shared';
import {GetOrganizationDevices} from '../../../afb-api/actions/device.actions';

@Component({
  selector: 'awair-floor-plan-editor-sidebar',
  templateUrl: './floor-plan-editor-sidebar.component.html',
  styleUrls: ['./floor-plan-editor-sidebar.component.scss']
})

export class FloorPlanEditorSidebarComponent implements OnInit {

  selectedOrganization$: Observable<OrganizationWithToken> = this.rootStore.pipe(
    select(fromRoot.selectedOrganization),
    filter((selectedOrganization: OrganizationWithToken) => selectedOrganization != null)
  );

  selectedFloor$: Observable<Floor> = this.floorPlanEditorStore.pipe(
    select(fromFloorPlanEditor.selectedFloor)
  );
  floors$: Observable<Floor[]> = this.afbApiStore.pipe(
    select(fromAfbApi.floors),
    filter((floors: Floor[]) => floors != null)
  );
  hasFloor$: Observable<boolean> = this.afbApiStore.pipe(
    select(fromAfbApi.hasFloor)
  );
  isFloorPlanEdited$: Observable<boolean> = this.floorPlanEditorStore.pipe(
    select(fromFloorPlanEditor.isFloorPlanEdited)
  );

  devicesWithUuid$: Observable<DeviceWithUuid[]> = this.afbApiStore.pipe(
    select(fromAfbApi.devicesWithUuid),
    filter((devicesWithUuid: DeviceWithUuid[]) => devicesWithUuid != null)
  );
  deviceMarkerUuids$: Observable<string[]> = this.floorPlanEditorStore.pipe(
    select(fromFloorPlanEditor.deviceMarkerUuids),
    distinctUntilChanged(this.utilService.compareSortedArray)
  );
  devicesWithUuidAndStatus$: Observable<DeviceWithUuidAndStatus[]> =
    combineLatest(this.devicesWithUuid$, this.deviceMarkerUuids$,
      (devicesWithUuid: DeviceWithUuid[], markerUuids: string[]) =>
        devicesWithUuid.reduce((acc: DeviceWithUuidAndStatus[], deviceWithUuid: DeviceWithUuid) => {
          return [...acc, {...deviceWithUuid, isSelected: markerUuids.includes(deviceWithUuid.uuid)}]
        }, [])
    );

  // Variables for floor dialogs
  private createFloorDialog: MatDialogRef<CreateFloorDialogComponent>;
  private deleteFloorDialog: MatDialogRef<NegativeConfirmComponent>;
  private isFloorCreated$: Observable<boolean> = this.afbApiStore.pipe(
    select(fromAfbApi.isPostFloorLoaded),
    filter((isFloorCreated: boolean) => isFloorCreated != null && isFloorCreated)
  );
  private createFloorError$: Observable<string> = this.afbApiStore.pipe(
    select(fromAfbApi.postFloorError)
  );
  private isFloorDeleted$: Observable<boolean> = this.afbApiStore.pipe(
    select(fromAfbApi.isDeleteFloorLoaded),
    filter((isFloorDeleted: boolean) => isFloorDeleted != null && isFloorDeleted)
  );
  private deleteFloorError$: Observable<string> = this.afbApiStore.pipe(
    select(fromAfbApi.deleteFloorError)
  );

  constructor(private dialog: MatDialog,
              private afbApiStore: Store<fromAfbApi.State>,
              private rootStore: Store<fromRoot.State>,
              private floorPlanEditorStore: Store<fromFloorPlanEditor.State>,
              private floorPlanStore: Store<fromFloorPlan.State>,
              private authService: AuthService,
              private utilService: UtilService) {
  }

  ngOnInit() {
    this.isFloorCreated$.pipe(withLatestFrom(this.createFloorError$)).subscribe(
      ([isFloorCreated, createFloorError]: [boolean, string]) => {
        if (this.createFloorDialog && this.createFloorDialog.componentInstance) {
          this.createFloorDialog.componentInstance.isLoading = false;
          // Alert if there's error, if not, close the dialog
          createFloorError != null ? alert(createFloorError) : this.createFloorDialog.close();
        }
      }
    );
    this.isFloorDeleted$.pipe(withLatestFrom(this.deleteFloorError$, this.selectedOrganization$)).subscribe(
      ([isFloorDeleted, deleteFloorError, selectedOrganization]: [boolean, string, OrganizationWithToken]) => {
        if (this.deleteFloorDialog && this.deleteFloorDialog.componentInstance) {
          this.deleteFloorDialog.componentInstance.isLoading = false;
          // Update organization devices with new floor status
          this.afbApiStore.dispatch(new GetOrganizationDevices({
            organizationToken: selectedOrganization.token
          }));
          // Alert if there's error, if not, close the dialog
          deleteFloorError != null ? alert(deleteFloorError) : this.deleteFloorDialog.close();
        }
      }
    );
  }

  onCreateFloor(organizationToken: string): void {
    this.createFloorDialog = this.dialog.open(CreateFloorDialogComponent, {
      disableClose: false,
      hasBackdrop: true
    });
    this.createFloorDialog.componentInstance.create.subscribe((floorName: string) => {
      this.floorPlanStore.dispatch(new PostFloor({
        organizationToken,
        name: floorName
      }));
    });
  }

  onSelectFloor(selectedFloorId: string): void {
    this.floorPlanEditorStore.dispatch(new SelectFloor(selectedFloorId));
  }

  onDeleteFloor(floor: Floor, organizationToken: string): void {
    this.deleteFloorDialog = this.dialog.open(NegativeConfirmComponent, {
      disableClose: false
    });
    this.deleteFloorDialog.componentInstance.header = 'Are you sure?';
    this.deleteFloorDialog.componentInstance.subHeader = `This will delete ${floor.name} and remove its devices.`;
    this.deleteFloorDialog.componentInstance.btnText = 'Yes, Delete';
    this.deleteFloorDialog.componentInstance.confirm.subscribe(() => {
      this.afbApiStore.dispatch(new DeleteFloor({
        organizationToken,
        floorId: floor.id
      }));
    });
  }

  toFloorList(): void {
    this.floorPlanEditorStore.dispatch(new DeselectFloor());
    this.floorPlanEditorStore.dispatch(new RemoveAllDeviceMarker());
    this.floorPlanEditorStore.dispatch(new ResetFloorImage());
  }
}
